import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIdBadge,
  faCheck,
  faXmark,
  faCircleCheck,
  faTriangleExclamation,
  faRotate,
  faEyeSlash,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import TeacherCard from '../components/TeacherCard/TeacherCard';
import './TeacherProfiles.css';

/* =========================================================
   /Admin/teacher-profiles — "Meet our teachers" on the home page.
   - the switch for the whole section (shown only with 2+ approved teachers)
   - teachers' cards waiting for approval, with the card exactly as on the home page
     (and the one live now, when they edited an approved card)
   - approved cards (take one off the home page)
   Backend: backend/api/Teachers/PublicProfile.js
   ========================================================= */
const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const when = (v) => {
  const d = new Date(v);
  if (!v || Number.isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
};

const monthName = (ym) => {
  const m = /^(\d{4})-(\d{2})$/.exec(ym || '');
  return m ? new Date(Date.UTC(+m[1], +m[2] - 1, 1)).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }) : '';
};

/* "Teaching at RoboMind since" (month): the years on the card. Empty = from their first class on the website. */
const SinceField = ({ p, busy, onSave }) => (
  <label className="rmatp-since">
    <span>Teaching at RoboMind since</span>
    <input
      type="month"
      value={p.since || ''}
      max={new Date().toISOString().slice(0, 7)}
      disabled={busy}
      onChange={(e) => onSave(p, e.target.value)}
    />
    <small>
      {p.since
        ? `${p.draft.years ? `${p.draft.years} ${p.draft.years === 1 ? 'year' : 'years'} on the card` : 'Under 1 year: not shown'}`
        : p.firstClass
          ? `Empty: counted from their first class on the website (${monthName(p.firstClass)})`
          : 'Empty: no classes on the website yet'}
    </small>
  </label>
);

const STATUS_LABEL = { pending: 'Waiting', approved: 'On the home page', rejected: 'Change asked', hidden: 'Hidden by teacher', removed: 'Removed' };

const TeacherProfiles = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(`${API}/api/admin/teacher-profiles`, auth());
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'We couldn’t load the teacher profiles.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const act = async (p, action) => {
    let body = {};
    if (action === 'reject') {
      const note = window.prompt(`What should ${p.fullName} change? (sent to them as a notification)`, '');
      if (note === null) return;
      body = { note };
    }
    if (action === 'remove' && !window.confirm(`Take ${p.fullName} off the home page?`)) return;
    setBusy(p.id);
    try {
      await axios.post(`${API}/api/admin/teacher-profiles/${p.id}/${action}`, body, auth());
      setToast({ msg: { approve: `${p.fullName}: approved.`, reject: `${p.fullName}: change asked.`, remove: `${p.fullName}: taken off the home page.` }[action] });
      await load();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || 'Something went wrong.', bad: true });
    } finally {
      setBusy('');
    }
  };

  const saveSince = async (p, since) => {
    setBusy(p.id);
    try {
      await axios.put(`${API}/api/admin/teacher-profiles/${p.id}/since`, { since }, auth());
      await load();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || 'Couldn’t save the date.', bad: true });
    } finally {
      setBusy('');
    }
  };

  const setSection = async (visible) => {
    setBusy('section');
    try {
      await axios.put(`${API}/api/admin/teacher-profiles/section`, { visible }, auth());
      await load();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || 'Couldn’t save.', bad: true });
    } finally {
      setBusy('');
    }
  };

  const items = data?.items || [];
  const waiting = items.filter((x) => x.status === 'pending');
  const live = items.filter((x) => x.live && x.status !== 'pending');
  const section = data?.section;

  return (
    <div className="rmatp">
      <header className="rmatp-head">
        <h1>
          <FontAwesomeIcon icon={faIdBadge} /> Teacher profiles
        </h1>
        <p>Teachers fill their card in their Settings. Check it here before it shows in “Meet our teachers” on the home page.</p>
      </header>

      {error && !data ? (
        <div className="rmatp-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <p>{error}</p>
          <button type="button" className="rmatp-btn" onClick={load}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </div>
      ) : !data ? (
        <div className="rmatp-skel" aria-busy="true" />
      ) : (
        <>
          <section className="rmatp-section">
            <label className="rmatp-switch">
              <input type="checkbox" checked={section.visible} disabled={busy === 'section'} onChange={(e) => setSection(e.target.checked)} />
              <span className="rmatp-switch-ui" aria-hidden="true" />
              <span>
                <strong>Show “Meet our teachers” on the home page</strong>
                <small>
                  {section.onSite
                    ? `On the home page now, with ${section.shown} teachers.`
                    : section.visible
                      ? `Turned on, but hidden until ${section.min} teachers are approved (${section.shown} now).`
                      : `Off. ${section.shown} approved ${section.shown === 1 ? 'teacher' : 'teachers'} so far.`}
                </small>
              </span>
            </label>
          </section>

          <h2 className="rmatp-h2">
            Waiting for you <span>{waiting.length}</span>
          </h2>
          {waiting.length === 0 ? (
            <p className="rmatp-none">
              <FontAwesomeIcon icon={faCircleCheck} /> Nothing to approve.
            </p>
          ) : (
            <ul className="rmatp-list">
              {waiting.map((p) => (
                <li key={p.id} className="rmatp-item">
                  <div className="rmatp-who">
                    <a href={`/Admin/teacherprofile/${p.id}`} target="_blank" rel="noopener noreferrer">
                      {p.fullName}
                    </a>
                    {!p.active && <span className="rmatp-tag is-off">Inactive</span>}
                    <small>{when(p.submittedAt)}</small>
                  </div>
                  <div className={`rmatp-cards ${p.live ? 'is-two' : ''}`}>
                    {p.live && (
                      <>
                        <figure>
                          <figcaption>On the home page now</figcaption>
                          <div className="rmatp-stage">
                            <TeacherCard t={p.live} />
                          </div>
                        </figure>
                        <FontAwesomeIcon icon={faArrowRight} className="rmatp-arrow" />
                      </>
                    )}
                    <figure>
                      <figcaption>{p.live ? 'New version' : 'How it will look'}</figcaption>
                      <div className="rmatp-stage">
                        <TeacherCard t={p.draft} />
                      </div>
                    </figure>
                  </div>
                  <SinceField p={p} busy={busy === p.id} onSave={saveSince} />
                  <div className="rmatp-actions">
                    <button type="button" className="rmatp-btn rmatp-btn--ghost" disabled={busy === p.id} onClick={() => act(p, 'reject')}>
                      <FontAwesomeIcon icon={faXmark} /> Ask for a change
                    </button>
                    <button type="button" className="rmatp-btn" disabled={busy === p.id} onClick={() => act(p, 'approve')}>
                      <FontAwesomeIcon icon={faCheck} /> Approve
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <h2 className="rmatp-h2">
            On the home page <span>{live.length}</span>
          </h2>
          {live.length === 0 ? (
            <p className="rmatp-none">No approved teachers yet.</p>
          ) : (
            <ul className="rmatp-grid">
              {live.map((p) => (
                <li key={p.id} className="rmatp-live">
                  <div className="rmatp-stage">
                    <TeacherCard t={p.live} />
                  </div>
                  <div className="rmatp-live-foot">
                    <span>
                      {p.fullName}
                      {!p.active && <span className="rmatp-tag is-off">Inactive: not shown</span>}
                      {p.status === 'rejected' && <span className="rmatp-tag">{STATUS_LABEL.rejected}</span>}
                    </span>
                    <button type="button" className="rmatp-link" disabled={busy === p.id} onClick={() => act(p, 'remove')}>
                      <FontAwesomeIcon icon={faEyeSlash} /> Remove
                    </button>
                  </div>
                  <SinceField p={p} busy={busy === p.id} onSave={saveSince} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {toast && (
        <div className={`rmatp-toast ${toast.bad ? 'is-bad' : ''}`} role="status">
          <FontAwesomeIcon icon={toast.bad ? faTriangleExclamation : faCircleCheck} /> {toast.msg}
        </div>
      )}
    </div>
  );
};

export default TeacherProfiles;
